import {
  Card as MuiCard,
  Box,
  useTheme,
  styled,
  Link,
  Grid,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import kebabCase from 'lodash/kebabCase';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {useMemo, useState, useEffect} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {jobDetailService} from '@jobseeker/common-core/services';

import type {JobDetailCardProps} from '../type';
import {
  YellowCrown,
  DefaultJobLogo,
  Dot,
  CautionIcon,
  GreyDivider,
  StarIconV2,
  TopStar,
  TopRecrBadge3,
  TopRecrBadge10p,
  TopRecrBadge10,
  TopRecrBadge5p,
  TopRecrBadge5,
} from '@jobseeker/lotus/icons';
import {getRandomColor} from '@jobseeker/lotus/theme';
import JobTag from '@jobseeker/lotus/components/JobList/V2/JobTags';
import {
  ImageWithFallback,
  Typography,
  JoblistPaper,
} from '@jobseeker/lotus/components/JobList/V2';
import {dateToDuration} from '@jobseeker/lotus/utils/date';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {Tooltip} from '@jobseeker/lotus/elements';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {cleanUrl} from '@jobseeker/lotus/utils';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const prevDate = new Date();
prevDate.setMonth(prevDate.getMonth() - 3);

const JobDetailCardComponent = (props: JobDetailCardProps) => {
  const {whatFor = ''} = props;
  const forInterviewsPage = whatFor === 'interviews';
  const forCourseDetailPage = whatFor === 'course-detail';

  const IsLarge = useIsLargeDevice('sm');
  const theme = useTheme();
  const {date, jobData} = props;
  const [oldWarning, setOldWarning] = useState(false);
  const getDeviceOs = (userAgent: string): string => {
    const isMobile =
      /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
        userAgent
      ) || false;

    if (isMobile) {
      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
        return 'ios';
      }
    }

    return 'web';
  };
  const useUserAgent = () => {
    const [userAgent, setUserAgent] = useState<string>('');

    useEffect(() => {
      if (typeof navigator !== 'undefined') {
        setUserAgent(navigator.userAgent || navigator.vendor || '');
      }
    }, []);

    return userAgent;
  };
  const userAgent = useUserAgent();

  useEffect(() => {
    if (new Date(date) <= prevDate) {
      setOldWarning(true);
    }
  }, [date]);

  const dateString = useMemo(() => {
    return dateToDuration(date, true);
  }, [date]);
  const randomColor = useMemo(
    () => getRandomColor(props.jobData?.id),
    [props.companyImgUrl, props.jobData?.companyData]
  );
  const MainComponent = Card;
  const slug = jobData?.companyData?.ambitionBoxInfo?.seo?.slug;

  // useEffect(() => {
  //   const fetchSlug = async () => {
  //     try {
  //       const response = await jobDetailService.getCompanySlugById(
  //         jobData?.recruiter?.centralizedCompanyId || -1
  //       );
  //       setSlug(response?.data?.slug); // Update the state with the fetched slug
  //     } catch (error) {
  //       console.error('Error fetching slug:', error);
  //     }
  //   };

  //   if (jobData?.recruiter?.centralizedCompanyId) {
  //     fetchSlug();
  //   }
  // }, [jobData]); // Runs whenever `jobData` changes

  const companyPageRedirect = async () => {
    if (slug) {
      TrackEventUBA('companyDetailsClick', 'click', {
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      openUrl(
        `/company/${slug}-careers?ref=jdp&utm_source=${
          process.env.NEXT_PUBLIC_APP_NAME
        }&utm_medium=${getDeviceOs(userAgent)}&utm_campaign=${
          process.env.NEXT_PUBLIC_APP_NAME
        }_jd_ratings`
      );
    }
  };
  const ambitionBoxInfo = props.jobData?.companyData?.ambitionBoxInfo;
  const showcasePage = props.jobData?.showcase;
  const createdTimeMs = props.jobData?.createdTimeMs;

  const rating = ambitionBoxInfo?.aggregateRatingOneDecimal;
  const reviewsCount = ambitionBoxInfo?.reviewsCount;

  const isRatingValid =
    typeof rating === 'number' && rating >= (props.jobData?.minRatingAb || 0);
  const hasReviews = typeof reviewsCount === 'number' && reviewsCount > 0;
  const isNotShowcase = !showcasePage;
  const isActiveJob =
    typeof createdTimeMs === 'number' &&
    Date.now() - createdTimeMs <= 180 * 24 * 60 * 60 * 1000;

  const showRating =
    ambitionBoxInfo &&
    isRatingValid &&
    hasReviews &&
    isNotShowcase &&
    isActiveJob;

  const imageLoader = ({src}: {src: string}) => {
    return props.companyImgUrl ?? '';
  };

  const getLocationParsed = (location: string) => {
    const locationMapping: {[key: string]: string} = {
      ['gurgaon']: 'gurgaon-gurugram',
      ['gurugram']: 'gurgaon-gurugram',
      ['cochin']: 'cochin-kochi',
      ['kochi']: 'cochin-kochi',
      ['vishakhapatnam']: 'vishakhapatnam-vizag',
      ['vizag']: 'vishakhapatnam-vizag',
      ['trivandrum']: 'trivandrum-thiruvananthapuram',
      ['thiruvananthapuram']: 'trivandrum-thiruvananthapuram',
      ['vadodara']: 'vadodara-baroda',
      ['baroda']: 'vadodara-baroda',
      ['varanasi']: 'varanasi-banaras',
      ['banaras']: 'varanasi-banaras',
    };

    return (
      locationMapping[location] ?? location.toLowerCase().replace(/\s+/g, '-')
    );
  };

  const LocationCategoryUrl = ({locations}: {locations: string}) => {
    const destructedLocations = locations?.split('/');
    return (
      <>
        {destructedLocations?.map((location, index) => (
          <a
            key={`${location}-${index}-cat-page`}
            href={`/${props?.category?.tag
              ?.toLowerCase()
              .replace(/[^A-Z0-9]+/gi, '-')
              .replace(/-+/g, '-')}-jobs-in-${getLocationParsed(
              location.toLowerCase()
            )}`}
            target="_blank"
            style={{
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {index > 0 ? `/${location}` : location}
          </a>
        ))}
      </>
    );
  };
  const companyPageUrl =
    slug && slug?.length > 0
      ? `${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${cleanUrl(slug)}-careers`
      : '#';

  const yrs = jobData?.recruiter?.firstRecrTime || 0;
  const badge =
    yrs > 10
      ? TopRecrBadge10p
      : yrs == 10
        ? TopRecrBadge10
        : yrs > 5
          ? TopRecrBadge5p
          : yrs == 5
            ? TopRecrBadge5
            : TopRecrBadge3;

  return (
    <MainComponent
      color="gray.dark"
      sx={{
        position: 'relative',
        transition: 'all ease 0.2s',
        boxShadow: forInterviewsPage || props.isConsultant ? 'none' : undefined,
        overflow: 'visible',
        ...props.sx,
      }}
    >
      {props.jobData?.showcase?.showcaseImages?.length && !props?.brandedJD && (
        <Box sx={{position: 'relative', mb: 0.9}}>
          <Grid
            container
            gap={{xxs: 0.3, md: 0.8}}
            sx={{
              '.showcaseImage': {
                '& img': {borderRadius: 2},
              },
              '.showcaseImage:first-child': {
                '& img': {borderRadius: '8px 8px 8px 0px'},
              },
              '.showcaseImage:last-child': {
                '& img': {borderRadius: '8px 8px 0px 8px'},
              },
            }}
          >
            {props.jobData?.showcase?.showcaseImages
              ?.slice(0, 3)
              .map((img, i) => (
                <Grid
                  item
                  xs
                  key={`showcaseImage-${i}`}
                  className="showcaseImage"
                >
                  <Box
                    // width={'100%'}
                    height={{xxs: 96, sm: 230}}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    <Image
                      src={img}
                      alt={
                        props.jobData?.showcase?.showcaseName ??
                        'showcase image'
                      }
                      placeholder="blur"
                      blurDataURL={img}
                      layout="fill"
                      quality={50}
                      // layout="responsive"
                      objectFit="cover"
                      key={`jobdetail-v2-showcase-${i}`}
                      onClick={event => {
                        event?.preventDefault();
                        openUrl(
                          `${props.jobData?.showcase?.showcaseUrl}?ref=jdp` ||
                            '#',
                          '_blank'
                        );
                        if (props.onUbaClick) {
                          props.onUbaClick();
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Grid>
              ))}
          </Grid>

          <Box
            sx={{
              display: 'flex',
              position: 'absolute',
              zIndex: 1,
              left: '24px',
              bottom: '-20px',
              '.joblist__logo': {
                borderRadius: 2,
                overflow: 'hidden',
                background: '#fff',
                boxShadow: 2,
                border: '0.6px solid #7E7E7E33',
              },
            }}
          >
            {props.jobData?.companyData?.companyLogoFilePath ||
            (forCourseDetailPage && props?.companyImgUrl) ? (
              <Box
                position={'relative'}
                sx={{
                  maxWidth: {xxs: 50, md: 80},
                  maxHeight: {xxs: 50, md: 80},
                }}
              >
                <ImageWithFallback
                  className="joblist__logo"
                  // src={props.companyImgUrl ?? ''}
                  // fallback={props.fallbackCompanyUrl}

                  // src={props.companyImgUrl ?? ''}
                  // fallback={props.fallbackCompanyUrl}
                  // blurDataURL={props.companyImgUrl}
                  // loader={imageLoader}

                  src={
                    props.jobData?.companyData?.companyLogoFilePath ??
                    (forCourseDetailPage ? (props?.companyImgUrl ?? '') : '')
                  }
                  fallback={props.fallbackCompanyUrl}
                  blurDataURL={
                    props.jobData?.companyData?.companyLogoFilePath ||
                    props.companyImgUrl ||
                    ''
                  }
                  alt={`${jobData?.companyData?.companyName?.replace(
                    /\s+/g,
                    ''
                  )}_logo'`}
                  width={IsLarge ? 80 : 50}
                  height={IsLarge ? 80 : 50}
                  object-fit="cover"
                  quality={50}
                  placeholder="blur"
                  itemId={jobData?.id}
                  // loader={imageLoader}
                />
              </Box>
            ) : (
              <Box
                className="joblist__logo"
                sx={{
                  background: randomColor,
                  p: theme.spacing(1.5),
                  height: 'min-content',
                  lineHeight: 0,
                }}
              >
                <Image
                  src={DefaultJobLogo}
                  alt={'Logo'}
                  width="40"
                  height={'40'}
                  objectFit="cover"
                  quality={50}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Box>
        <JoblistPaper
          brandedJD={props?.brandedJD}
          elevation={0}
          sx={{
            ...props.sx,
            py: {xxs: 2, md: forInterviewsPage ? 2 : props?.brandedJD ? 2 : 3},
            px: {xxs: 1.5, md: props?.brandedJD ? 2.5 : 2},
          }}
        >
          {(!props.jobData?.showcase?.showcaseImages?.length ||
            props?.brandedJD) &&
            !props.isConsultant && (
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                {/* {props.jobData?.companyData?.companyLogoFilePath ? ( */}
                {(forCourseDetailPage && props?.companyImgUrl) ||
                (!props.jobData?.confidential &&
                  props.jobData?.companyData?.companyLogoFilePath) ? (
                  <ImageWithFallback
                    className="joblist__logo"
                    // src={props.companyImgUrl ?? ''}
                    // fallback={props.fallbackCompanyUrl}
                    // blurDataURL={props.companyImgUrl}

                    src={
                      props.jobData?.companyData?.companyLogoFilePath ??
                      (forCourseDetailPage ? (props?.companyImgUrl ?? '') : '')
                    }
                    fallback={props.fallbackCompanyUrl}
                    blurDataURL={
                      props.jobData?.companyData?.companyLogoFilePath ||
                      props.companyImgUrl ||
                      ''
                    }
                    alt={`${jobData?.companyData?.companyName?.replace(
                      /\s+/g,
                      ''
                    )}_logo'`}
                    width={props?.brandedJD ? (IsLarge ? '80' : '50') : '50'}
                    style={{
                      cursor: slug ? 'pointer' : 'default',
                    }}
                    onClick={companyPageRedirect}
                    height={props?.brandedJD ? (IsLarge ? '80' : '50') : '50'}
                    objectFit="cover"
                    quality={50}
                    placeholder="blur"
                    itemId={jobData?.id}
                    brandedJD={props?.brandedJD}
                    // loader={imageLoader}
                  />
                ) : (
                  <Box
                    className="joblist__logo"
                    sx={{
                      background: randomColor,
                      p: theme.spacing(1.5),
                      marginTop: props?.brandedJD
                        ? IsLarge
                          ? '-35px'
                          : '0px'
                        : '0',

                      height: 'min-content',
                      lineHeight: 0,
                    }}
                  >
                    <Image
                      src={DefaultJobLogo}
                      alt={'Logo'}
                      width={props?.brandedJD ? (IsLarge ? '56' : '26') : '25'}
                      height={props?.brandedJD ? (IsLarge ? '56' : '26') : '25'}
                      objectFit="cover"
                      quality={50}
                    />
                  </Box>
                )}
              </Box>
            )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '100%',
              rowGap: '5px',
            }}
          >
            <Box
              // sx={{pr: {xxs: 3, sm: 12.2}}}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'flex-start'}
                gap={'2px'}
                flexWrap={'wrap'}
              >
                <Typography
                  component={'h1'}
                  variant="subtitle2"
                  fontSize={{xxs: 14, md: forInterviewsPage ? 14 : 16}}
                  fontWeight={500}
                  lineHeight={1.4}
                >
                  {props.title}
                </Typography>
                {oldWarning && (
                  <Tooltip
                    arrow
                    title={
                      <Typography
                        variant="subtitle3"
                        lineHeight={2.25}
                        color="secondary.main"
                      >
                        This job opening was posted a long time ago. It may no
                        longer be active, and it hasn't been removed by the
                        recruiter. Please use your discretion before applying.
                      </Typography>
                    }
                  >
                    <Image
                      src={CautionIcon}
                      alt={'caution'}
                      data-name="old-job"
                      width={32}
                      height={32}
                      objectFit="cover"
                      quality={50}
                    />
                  </Tooltip>
                )}
              </Box>
              {props.isPremiumJob && (
                <Box
                  sx={{
                    // position: 'absolute',
                    right: {xxs: -10, sm: -5},
                    top: {xxs: -5, sm: 16},
                    width: 16,
                    height: 16,
                  }}
                >
                  <Image
                    className="joblist__premium_icon"
                    src={YellowCrown}
                    alt={'premium_icon'}
                    width={IsLarge ? 20 : 16}
                    height={IsLarge ? 20 : 16}
                    objectFit="cover"
                    quality={50}
                  />
                </Box>
              )}
            </Box>
            <Box
              className="joblist__subtitle"
              sx={{
                display: 'flex',
                columnGap: 1,
                rowGap: 0.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                color: theme.palette.text.secondary,
                flexWrap: 'nowrap',
                mb: '-2px',
              }}
            >
              <Box
                display={'flex'}
                flexWrap={'wrap'}
                gap={1}
                flexDirection={{xxs: 'column', md: 'row'}}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pb: forInterviewsPage ? 0.5 : 0,
                  }}
                  flexWrap={'wrap'}
                >
                  {companyPageUrl !== '#' ? (
                    <Link
                      href={companyPageUrl}
                      target="_blank"
                      onClick={e => {
                        e.preventDefault();
                        setTimeout(() => openUrl(companyPageUrl, '_blank'), 0);
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: 'inherit',
                        textDecoration: 'none',
                      }}
                    >
                      <Typography
                        component={'span'}
                        variant="subtitle3"
                        fontSize={{xxs: 12, md: forInterviewsPage ? 12 : 14}}
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {props?.companyName}
                      </Typography>
                      <OpenInNewIcon sx={{fontSize: 16, ml: 0.5}} />
                    </Link>
                  ) : (
                    <Typography
                      component={'span'}
                      variant="subtitle3"
                      fontSize={{xxs: 12, md: forInterviewsPage ? 12 : 14}}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {props?.companyName}
                    </Typography>
                  )}
                  <Image
                    src={Dot}
                    width={4}
                    height={4}
                    alt="."
                    style={{margin: '2px 8px'}}
                  />
                  <Typography
                    component={'span'}
                    variant="subtitle3"
                    fontSize={{xxs: 12, md: forInterviewsPage ? 12 : 14}}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {props.experiance}
                    <Image
                      src={Dot}
                      width={4}
                      height={4}
                      alt="."
                      style={{margin: '2px 8px'}}
                    />
                    {forCourseDetailPage ? (
                      <>{props.location}</>
                    ) : (
                      <LocationCategoryUrl locations={props.location} />
                    )}
                    {!props.jobData?.confidential && showRating && (
                      <Box
                        display={{xxs: 'none', md: 'flex'}}
                        alignItems="center"
                      >
                        <Image
                          src={Dot}
                          alt="."
                          width={4}
                          height={4}
                          style={{margin: '0 8px'}}
                        />

                        <Tooltip
                          arrow
                          placement="bottom-start"
                          title={
                            <Typography
                              variant="subtitle3"
                              lineHeight={2.25}
                              color="secondary.main"
                            >
                              Powered By AmbitionBox
                            </Typography>
                          }
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{cursor: 'pointer'}}
                          >
                            <Image
                              src={StarIconV2}
                              height={14}
                              width={14}
                              alt="star-icon"
                            />

                            {props.jobData?.showcase?.showcaseUrl || slug ? (
                              props.jobData?.showcase?.showcaseUrl ? (
                                <Link
                                  href={`${
                                    props.jobData?.showcase?.showcaseUrl
                                  }?ref=jdp&utm_source=${
                                    process.env.NEXT_PUBLIC_APP_NAME
                                  }&utm_medium=${getDeviceOs(
                                    userAgent
                                  )}&utm_campaign=${
                                    process.env.NEXT_PUBLIC_APP_NAME
                                  }_jd_ratings`}
                                  target="__blank"
                                  style={{textDecoration: 'none'}}
                                >
                                  <Typography
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '14px',
                                      fontStyle: 'normal',
                                      fontWeight: 500,
                                      lineHeight: '18px',
                                      ml: '4px',
                                    }}
                                  >
                                    {rating}
                                  </Typography>
                                </Link>
                              ) : (
                                <Typography
                                  onClick={companyPageRedirect}
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '18px',
                                    ml: '4px',
                                  }}
                                >
                                  {rating}
                                </Typography>
                              )
                            ) : null}

                            <Image
                              src={GreyDivider}
                              height={14}
                              width={10}
                              alt="grey-divider"
                            />

                            {props.jobData?.showcase?.showcaseUrl || slug ? (
                              props.jobData?.showcase?.showcaseUrl ? (
                                <Link
                                  href={`${props.jobData?.showcase?.showcaseUrl}?ref=jdp`}
                                  target="__blank"
                                  style={{textDecoration: 'none'}}
                                >
                                  <Typography
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '14px',
                                      fontStyle: 'normal',
                                      fontWeight: 500,
                                      lineHeight: '18px',
                                    }}
                                  >
                                    {`${reviewsCount.toLocaleString()}+ Reviews`}
                                  </Typography>
                                </Link>
                              ) : (
                                <Typography
                                  onClick={companyPageRedirect}
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '18px',
                                  }}
                                >
                                  {`${reviewsCount.toLocaleString()}+ Reviews`}
                                </Typography>
                              )
                            ) : null}
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Typography>
                </Box>
                {<JobTag tags={props.additionalInfo} />}
              </Box>
              {!forInterviewsPage && !forCourseDetailPage && (
                <Typography
                  component={'span'}
                  color="text.secondary"
                  variant="body3"
                  display={{xxs: 'none', md: 'flex'}}
                  m={0}
                  minWidth={'150px'}
                  alignItems={'center'}
                  justifyContent={'flex-end'}
                >
                  Posted {dateString}
                </Typography>
              )}
            </Box>
            <Box
              display={{xxs: 'flex', md: 'none'}}
              justifyContent={'space-between'}
              flexWrap="wrap"
              alignItems="center"
              columnGap="4px"
              rowGap="4px"
              width="100%"
            >
              {!showRating && (
                <Box>
                  {!forInterviewsPage && !forCourseDetailPage && (
                    <Box
                      sx={{
                        width: '100%',
                        mt: 0,
                        display: {xxs: 'flex', md: 'none'},
                      }}
                    >
                      <Typography
                        component={'span'}
                        color="text.secondary"
                        variant="body3"
                        m={0}
                      >
                        Posted {dateString}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {showRating && (
                <Box>
                  {!forInterviewsPage && !forCourseDetailPage && (
                    <Box
                      sx={{
                        width: '100%',
                        mt: 0,
                        display: {xxs: 'flex', md: 'none'},
                        marginTop: '6px',
                      }}
                    >
                      <Typography
                        component={'span'}
                        color="text.secondary"
                        variant="body3"
                        m={0}
                        x={{
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flexShrink: 1,
                        }}
                      >
                        Posted {dateString}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {!props.jobData?.confidential && showRating && (
                <Box display="flex" alignItems="center" marginTop="6px">
                  <Tooltip
                    arrow
                    placement="bottom-start"
                    title={
                      <Typography
                        variant="subtitle3"
                        lineHeight={2.25}
                        color="secondary.main"
                      >
                        Powered By AmbitionBox
                      </Typography>
                    }
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{cursor: 'pointer'}}
                    >
                      <Image
                        src={StarIconV2}
                        height={12}
                        width={12}
                        alt="star-icon"
                      />

                      {(props.jobData?.showcase?.showcaseUrl || slug) &&
                        (props.jobData?.showcase?.showcaseUrl ? (
                          <Link
                            href={`${
                              props.jobData?.showcase?.showcaseUrl
                            }?ref=jdp&utm_source=${
                              process.env.NEXT_PUBLIC_APP_NAME
                            }&utm_medium=${getDeviceOs(
                              userAgent
                            )}&utm_campaign=${
                              process.env.NEXT_PUBLIC_APP_NAME
                            }_jd_ratings`}
                            target="__blank"
                            style={{textDecoration: 'none'}}
                          >
                            <Typography
                              sx={{
                                color: 'text.secondary',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '18px',
                                ml: '4px',
                              }}
                            >
                              {rating}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography
                            onClick={companyPageRedirect}
                            sx={{
                              color: 'text.secondary',
                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              lineHeight: '18px',
                              ml: '4px',
                            }}
                          >
                            {rating}
                          </Typography>
                        ))}

                      <Image
                        src={GreyDivider}
                        height={14}
                        width={10}
                        alt="grey-divider"
                        style={{marginLeft: '2px'}}
                      />

                      {(props.jobData?.showcase?.showcaseUrl || slug) &&
                        (props.jobData?.showcase?.showcaseUrl ? (
                          <Link
                            href={`${props.jobData?.showcase?.showcaseUrl}?ref=jdp`}
                            target="__blank"
                            style={{textDecoration: 'none'}}
                          >
                            <Typography
                              sx={{
                                color: 'text.secondary',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '18px',
                              }}
                            >
                              {`${reviewsCount.toLocaleString()}+ Reviews`}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography
                            onClick={companyPageRedirect}
                            sx={{
                              color: 'text.secondary',
                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              lineHeight: '18px',
                            }}
                          >
                            {`${reviewsCount.toLocaleString()}+ Reviews`}
                          </Typography>
                        ))}
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {/* {props.jobData?.showcase?.showcaseUrl ? (
              <Link
                href={`${props.jobData?.showcase?.showcaseUrl}?ref=jdp`}
                target="__blank"
                sx={{
                  textDecoration: 'none',
                  mt: 1.5,
                  display: {xxs: 'none', md: 'flex'},
                  width: 'fit-content',
                }}
              >
                <Typography variant="subtitle3">
                  Visit Company Profile14px
                </Typography>
              </Link>
            ) : (
              slug && (
                <Typography
                  variant="subtitle3"
                  onClick={companyPageRedirect}
                  color="var(--Primary-Brand-Color, #49966E)"
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    mt: 0.5,
                    // display: {xxs: 'flex', md: 'flex'},
                    display: 'inline-flex',
                    width: 'fit-content',
                  }}
                >
                  Visit Company Profile
                </Typography>
              )
            )} */}
          </Box>
        </JoblistPaper>
        {!!props.jobData?.tags?.length && (
          <Box
            sx={{
              p: 2,
              pt: {xxs: props?.brandedJD ? 0 : 2, md: props?.brandedJD ? 0 : 2},
              borderTop: {
                xxs: props?.brandedJD ? 'none' : '1px solid #7E7E7E33',
                md: props?.brandedJD ? 'none' : '1px solid #7E7E7E33',
              },
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 0.7,
            }}
          >
            {props.jobData?.tags?.map(
              tag =>
                tag?.name && (
                  <Link
                    href={`/k/${kebabCase(tag.name)}-jobs`}
                    key={`TAG#${tag.id}`}
                    // component={NextLink}
                    sx={{
                      textDecoration: 'none',
                      color: 'text.primary',
                      transition: 'all ease 0.3s',
                      ':hover': {color: 'primary.main'},
                    }}
                  >
                    <Typography
                      component="span"
                      variant={'caption'}
                      fontSize={12}
                    >
                      #{tag.name}
                    </Typography>
                  </Link>
                )
            )}
          </Box>
        )}
      </Box>
      {props.isConsultant && (
        <Box position="relative">
          <Card
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(180, 185, 216, 0.32)',
              py: 1.5,
              px: {xxs: 1.5, md: 2.5},
              borderRadius: 0,
              borderBottomLeftRadius: {xxs: 0, md: 16},
              borderBottomRightRadius: {xxs: 0, md: 16},
            }}
          >
            {/* LEFT SECTION */}
            <Box
              display="flex"
              alignItems={{xxs: 'start', md: 'center'}}
              gap={1}
            >
              {(!props.jobData?.showcase?.showcaseImages?.length ||
                props?.brandedJD) && (
                <Box
                  sx={{
                    '.joblist__logo': {
                      borderRadius: 3,
                      overflow: 'hidden',
                      background: props?.jobData?.companyData
                        ?.companyLogoFilePath
                        ? '#fff'
                        : 'auto',
                      boxShadow: 2,
                      border: '0.6px solid #7E7E7E33',
                    },
                    height: 50,
                  }}
                  borderRadius={3}
                >
                  {/* {props.jobData?.companyData?.companyLogoFilePath ? ( */}
                  {(forCourseDetailPage && props?.companyImgUrl) ||
                  (!props.jobData?.confidential &&
                    props.jobData?.companyData?.companyLogoFilePath) ? (
                    <ImageWithFallback
                      className="joblist__logo"
                      // src={props.companyImgUrl ?? ''}
                      // fallback={props.fallbackCompanyUrl}
                      // blurDataURL={props.companyImgUrl}

                      src={
                        props.jobData?.companyData?.companyLogoFilePath ??
                        (forCourseDetailPage
                          ? (props?.companyImgUrl ?? '')
                          : '')
                      }
                      fallback={props.fallbackCompanyUrl}
                      blurDataURL={
                        props.jobData?.companyData?.companyLogoFilePath ||
                        props.companyImgUrl ||
                        ''
                      }
                      alt={`${jobData?.companyData?.companyName?.replace(
                        /\s+/g,
                        ''
                      )}_logo'`}
                      width={'50'}
                      style={{
                        cursor: slug ? 'pointer' : 'default',
                        borderRadius: 3,
                      }}
                      onClick={companyPageRedirect}
                      height={'50'}
                      objectFit="cover"
                      quality={50}
                      placeholder="blur"
                      itemId={jobData?.id}
                      // loader={imageLoader}
                    />
                  ) : (
                    <Box
                      className="joblist__logo"
                      sx={{
                        background: randomColor,
                        p: theme.spacing(1.5),

                        height: 'min-content',
                        lineHeight: 0,
                      }}
                    >
                      <Image
                        src={DefaultJobLogo}
                        alt={'Logo'}
                        width={'25'}
                        height={'25'}
                        objectFit="cover"
                        quality={50}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Text */}
              <Box>
                <Typography
                  fontSize={12}
                  fontWeight={{xxs: 400, md: 500}}
                  maxWidth={{xxs: '180px', md: 'none'}}
                >
                  {`Posted by ${props.jobData?.companyData?.companyName}`}
                </Typography>

                <Box
                  display={'flex'}
                  alignItems={'center'}
                  bgcolor={'#FFF'}
                  borderRadius={'10000px'}
                  px={1.25}
                  py={0.25}
                  border="1px solid var(--Colors-Alice-Blue-500, #F3F7FA)"
                  mt={0.5}
                  width={'fit-content'}
                >
                  <Image src={TopStar} height={14} width={14} alt="star" />
                  <Typography fontSize={10} fontWeight={400} ml={0.5}>
                    Top recruitment agency
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>

          {/* RIGHT BADGE */}
          <Box
            sx={{
              position: 'absolute',
              right: {xxs: 0, md: 16},
              top: '50%',
              transform: {
                xxs:
                  (props.jobData?.companyData?.companyName?.length || 0) > 18
                    ? 'translateY(-47%)'
                    : 'translateY(-43%)',
                md: 'translateY(-41%)',
              },
              width: 90,
              height: {xxs: 130, md: 150},
              zIndex: 2000,
            }}
          >
            <Image
              src={badge}
              alt="Top Recruitment Badge"
              width={90}
              height={IsLarge ? 150 : 135}
            />
          </Box>
        </Box>
      )}
    </MainComponent>
  );
};

export default JobDetailCardComponent;
